import { ApolloError, useMutation } from '@apollo/client';
import { NotificationPreferences } from '@bakerweb/models';
import { toast } from 'react-toastify';
import { CREATE_USER_BULK_MUTATION } from '../graphql/mutations/createUserBulk';
import { GET_USERS } from '../graphql/queries/getUsers';

type Response = {
  createUserBulk: {
    succeeded: string[];
    errors: {
      email: string;
      message: string;
    }[];
  };
};

export interface CreateUserBulk {
  emails: string[];
  portfolioCodes: string[];
  accounts: { id: string }[];
  notificationPreferences: Partial<Omit<NotificationPreferences, 'email'>>;
  permissions: {
    pledging: boolean;
    pledgeAndRelease: boolean;
    apm: boolean;
    files: boolean;
    cds: boolean;
    muniCredit: boolean;
  };
}

export const useCreateUserBulk = () => {
  const [createUserBulkMutation] = useMutation<Response>(CREATE_USER_BULK_MUTATION, {
    refetchQueries: [{ query: GET_USERS }]
  });

  const createUserBulk = async ({
    emails,
    portfolioCodes,
    accounts,
    notificationPreferences,
    permissions
  }: CreateUserBulk) => {
    const variables = {
      userBulkCreateDto: {
        emails,
        portfolioCodes,
        accounts,
        notificationPreferences,
        permissions
      }
    };

    try {
      const response = await createUserBulkMutation({ variables });
      if (response && response.data) {
        const { succeeded, errors } = response.data.createUserBulk;

        if (!errors.length) {
          succeeded.forEach((email) => toast.success(`User ${email} created successfully.`));
          return { error: false };
        }

        errors.forEach((error) => {
          toast.error(`There was an error creating user ${error.email}: ${error.message}`, { autoClose: false });
        });

        succeeded.forEach((email) => toast.success(`User ${email} created successfully`));
        // one or more failures
        return { error: true };
      }
      return { error: true };
    } catch (error) {
      toast.error((error as ApolloError).message);
      return { error: true };
    }
  };

  return { createUserBulk };
};
